import { activityLabelsById } from '@bioliberty/core';
import { Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartColours } from '../../../constants';
import { useActivityTypeChoices } from '../../../hooks/useActivityTypeChoices';
import { cn } from '../../../util';
import { ActivityTypeTimeSeriesDataPoint } from '../hooks/useDashboardInsights';
import { BarStackLabel } from './BarStackLabel';
import { ChartTooltip } from './ChartTooltip';

export interface ActivityTypeTotalRepsTimeSeriesChart {
  data: Array<ActivityTypeTimeSeriesDataPoint>;
  tickFormatter?: (value: string, index: number) => string;
  className?: string;
}

export function ActivityTypeTotalRepsTimeSeriesChart({
  data,
  tickFormatter,
  className
}: ActivityTypeTotalRepsTimeSeriesChart) {
  const activityTypeChoices = useActivityTypeChoices();
  const stackKeys = activityTypeChoices.map(({ value }) => value);

  return (
    <div className={cn('h-[300px]', className)}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="horizontal" margin={{ top: 24, right: 40 }} accessibilityLayer>
          <XAxis
            dataKey="timestamp"
            type="category"
            tickLine={false}
            fontSize={12}
            axisLine={false}
            stroke="#fff"
            minTickGap={35}
            interval="preserveStartEnd"
            tickMargin={12}
            tickFormatter={tickFormatter}
          />

          <YAxis type="number" axisLine={false} tickLine={false} stroke="#fff" fontSize={12} width={40} />

          <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.2} stroke="#fff" />

          {stackKeys.map((stackKey, index) => (
            <Bar
              stackId="activityTypes"
              key={stackKey}
              dataKey={stackKey}
              name={activityLabelsById[stackKey]}
              fill={chartColours[index]}
              isAnimationActive={false}
              radius={0}
            >
              <LabelList
                dataKey={stackKey ?? 'value'}
                fontSize={10}
                fill="#fff"
                content={({ value, x, y, width, height }) => {
                  return (
                    <BarStackLabel
                      value={value as number}
                      x={x as number}
                      y={y as number}
                      width={width as number}
                      height={height as number}
                      totalBarCount={data.length}
                    />
                  );
                }}
              />
            </Bar>
          ))}

          <Tooltip
            content={<ChartTooltip formatter={(value) => tickFormatter?.(value, 0) ?? ''} stacked />}
            cursor={{ fill: chartColours[0], fillOpacity: 0.1 }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
